import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Fingerprint, X, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISMISS_KEY = 'biometric_prompt_dismissed';
const PROMPT_DELAY_MS = 3000; // Show after 3 seconds

/**
 * BiometricPrompt - Shows a one-time prompt on mobile devices asking users
 * to enable biometric login (Face ID / Fingerprint).
 * Only shows on iOS/Android when biometric is not yet enabled.
 */
export const BiometricPrompt = () => {
  const [open, setOpen] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !token) return;

    // Check if user already dismissed this prompt
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) return;

    // Detect mobile device (iOS or Android)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // Also show on devices that support WebAuthn platform authenticators (laptops with Touch ID, etc.)
    const checkAndShow = async () => {
      let shouldShow = isMobile;

      // On desktop, check if platform authenticator is actually available
      if (!isMobile && window.PublicKeyCredential) {
        try {
          shouldShow = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch {
          shouldShow = false;
        }
      }

      if (!shouldShow) return;

      // Check if user already has biometric enabled
      try {
        const res = await fetch(`${API}/auth/webauthn/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.biometric_enabled) return; // Already enabled
        }
      } catch {
        return; // Can't check, don't show
      }

      // Show the prompt after a delay
      const timer = setTimeout(() => setOpen(true), PROMPT_DELAY_MS);
      return () => clearTimeout(timer);
    };

    checkAndShow();
  }, [user, token]);

  const handleDismiss = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, 'true');
  };

  const handleEnable = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, 'true');
    navigate('/settings');
  };

  if (!open) return null;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const biometricName = isIOS ? 'Face ID / Touch ID' : 'Fingerprint / Face Unlock';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="bg-[#2E3944] border-white/10 text-white max-w-sm mx-auto">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-[var(--brand)]/10 flex items-center justify-center">
            <Fingerprint className="w-8 h-8 text-[var(--brand)]" />
          </div>
          <DialogTitle className="text-xl font-serif text-white">
            Enable {biometricName}?
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm leading-relaxed">
            Log in faster and more securely using your device's biometric authentication. 
            Your biometric data never leaves your device.
          </DialogDescription>
        </DialogHeader>

        <div className="py-3 space-y-3">
          <div className="flex items-start gap-3 text-sm text-gray-300">
            <Shield className="w-4 h-4 text-[var(--brand)] mt-0.5 shrink-0" />
            <span>Instant login with just a look or touch</span>
          </div>
          <div className="flex items-start gap-3 text-sm text-gray-300">
            <Shield className="w-4 h-4 text-[var(--brand)] mt-0.5 shrink-0" />
            <span>More secure than passwords - phishing resistant</span>
          </div>
          <div className="flex items-start gap-3 text-sm text-gray-300">
            <Shield className="w-4 h-4 text-[var(--brand)] mt-0.5 shrink-0" />
            <span>Your biometric data stays on your device only</span>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            data-testid="biometric-prompt-enable"
            onClick={handleEnable}
            className="w-full btn-gold text-black font-semibold h-11"
          >
            <Fingerprint className="w-4 h-4 mr-2" />
            Enable {biometricName}
          </Button>
          <Button
            data-testid="biometric-prompt-dismiss"
            variant="ghost"
            onClick={handleDismiss}
            className="w-full text-gray-400 hover:text-white"
          >
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
